import { UserProfile } from 'fitbeat.gym-core';
import { translate } from 'react-i18next';
import { HeartRateStep } from 'fitbeat.models';
import React from 'react';
import {
    StyleProp, StyleSheet,
    TextStyle, ViewStyle,
} from 'react-native';
import { connect } from 'react-redux';

import {
    clearHeartRateDeviceList,
    disconnectHeartRateMonitor,
    getCorrectInitialSetupStep,
    goToSetupStep,
    removeHeartRateMonitor, removeHomeHeartRateMonitor,
    setFaqState,
    setThemeType, UpdateHeartRateMonitorsFn,
} from '../heartRateActions';
import {
  closeSetupScreen,
} from '../actions';
import {
  faqStateSelector,
  heartRateSetupStepSelector,
  themeTypeSelector,
} from '../selectors/heartRateMonitorSelectors';
import {
  HeartRateFaq,
} from './HeartRateFaq';

import Theme, { ThemeType } from '../../theme';
import { BaseModal } from '../../ui/components';
import { toggleHeartRateMonitorModalVisible } from '../../ui/redux';
import { CaptureHeartRateDeviceNumber } from './CaptureHeartRateDeviceNumber';
import { CaptureMetricInfo } from './CaptureMetricInfo';
import HasHeartRateDevice from './HasHeartRateDevice';
import TestHeartRateDevice from './TestHeartRateDevice';
import { defaultScale } from '../../utils/scaling';
import { DeviceSetupSubScreenHeader } from './DeviceSetup/DeviceSetupSubScreenHeader';

export enum ConsumerType {
    modal = 'Modal',
    nonModal = 'NonModal',
    homeWorkoutModal = 'homeWorkoutModal',
}

interface IProps {
    consumer: ConsumerType;
    themeType: ThemeType;
    userProfile: UserProfile;
    heartRateMonitorModalVisible: boolean;
    heartRateScanAction: any;
    initialStepForUsersWithBelts: HeartRateStep;
    heartRateSetupStep: HeartRateStep;
    faqState: any;
    changedTheme: any;
    t: (key: string) => string;
    updateUserHeartRateMonitors: UpdateHeartRateMonitorsFn;
    disconnectHrMonitor(): void;
    removeHrDeviceForUser(): void;
    toggleModal(visible: boolean): void;
    closeScreen(): void;
    userUpdateFunc(): any;
    getCorrectInitialStep(): void;
    goToStep(step: HeartRateStep): any;
    clearDeviceList(): void;
    showFaq(): void;
    setTheme(theme: any): void;
    setFaqActiveState(state: any): void;
}

class HeartRateMonitorSetupComponent extends React.PureComponent<IProps, any> {
    private steps: any = {
        [HeartRateStep.CAPTURE_METRIC_INFO]: {
            title: this.props.t('addHeartRateMonitor'),
            component: CaptureMetricInfo,
        },
        [HeartRateStep.CAPTURE_BELT]: {
            title: this.props.t('addHeartRateMonitor'),
            component: CaptureHeartRateDeviceNumber,
        },
        [HeartRateStep.HAS_BELT]: {
            title: this.props.t('heartRateMonitor'),
            component: HasHeartRateDevice,
        },
        [HeartRateStep.TEST_BELT]: {
            title: this.props.t('heartRateMonitor'),
            component: TestHeartRateDevice,
        },
        [HeartRateStep.FAQ]: {
            title: this.props.t('howToConnect'),
            component: HeartRateFaq,
        },
    };

    constructor(props: IProps) {
        super(props);
        props.getCorrectInitialStep();
    }

    public async componentDidMount() {
        const { themeType, setTheme } = this.props;
        setTheme(themeType);
    }

    public closeModal = () => {
        const {
            consumer, themeType, faqState,
            toggleModal, closeScreen, disconnectHrMonitor, getCorrectInitialStep,
            clearDeviceList, goToStep, setFaqActiveState, setTheme,
        } = this.props;

        if (faqState.isActive) {
            goToStep(faqState.stepBeforeFaq);
            setFaqActiveState({
                isActive: false,
                stepBeforeFaq: null,
            });
            setTheme(themeType);
            return;
        }

        clearDeviceList();
        getCorrectInitialStep();
        setTheme(themeType);

        if (consumer === ConsumerType.modal) {
            disconnectHrMonitor();
        }

        if (consumer === ConsumerType.modal ||
            consumer === ConsumerType.homeWorkoutModal) {
            toggleModal(false);
        } else {
            closeScreen();
        }
    }

    public removeDevice = async () => {
        const { removeHrDeviceForUser } = this.props;
        removeHrDeviceForUser();
    }

    public showFaq = () => {
        const { heartRateSetupStep, goToStep, setFaqActiveState, setTheme } = this.props;
        setFaqActiveState({
            isActive: true,
            stepBeforeFaq: heartRateSetupStep,
        });
        setTheme(ThemeType.dark);
        goToStep(HeartRateStep.FAQ);
    }

    public render() {
        const { heartRateMonitorModalVisible, userProfile, heartRateSetupStep, changedTheme, goToStep } = this.props;
        const Component = this.steps[heartRateSetupStep].component;
        const title = this.steps[heartRateSetupStep].title;
        const themeStyle = themeStyleMapper[changedTheme];

        if (this.props.consumer === ConsumerType.modal ||
            this.props.consumer === ConsumerType.homeWorkoutModal) {
            return (
                <BaseModal
                    title={title}
                    closeIconSize={35}
                    modalContainer={themeStyle.container}
                    isModalVisible={heartRateMonitorModalVisible}
                    headerTextStyle={styles.headerTextStyle}
                    headerTextColor={themeStyle.textColor4}
                    closeModal={this.closeModal}
                    containerStyle={styles.containerStyle}
                    bodyStyle={themeStyle.container}>
                    <Component
                        themeType={changedTheme}
                        goToStep={goToStep}
                        showFaq={this.showFaq}
                        userProfile={userProfile}
                        removeDevice={this.removeDevice}
                        closeModal={this.closeModal}
                        heartRateScanAction={this.props.heartRateScanAction}
                        updateUserHeartRateMonitors={this.props.updateUserHeartRateMonitors}
                    />
                </BaseModal>
            );
        } else {
            return (
                <>
                    <DeviceSetupSubScreenHeader title={title} onBackPress={this.closeModal} />
                    <Component
                        themeType={ThemeType.dark}
                        goToStep={goToStep}
                        showFaq={this.showFaq}
                        userProfile={userProfile}
                        removeDevice={this.removeDevice}
                        closeModal={this.closeModal}
                        heartRateScanAction={this.props.heartRateScanAction}
                        updateUserHeartRateMonitors={this.props.updateUserHeartRateMonitors}
                    />
                </>);
        }
    }
}

const styles = StyleSheet.create({
    modalContainer: {
        backgroundColor: Theme.WHITE,
    },
    headerTextStyle: {
        zIndex: 1,
        borderBottomWidth: 1,
        borderBottomColor: Theme.GREY_MEDIUM_LIGHT,
    },
    containerStyle: {
        height: '100vh',
    },
    headerRow: {
        padding: 0,
        justifyContent: 'space-between',
    },
    webviewLoading: {
        flex: 1,
        height: '100%',
        width: '100%',
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Theme.BLACK,
    },
});

export interface IThemeStyle {
    container: StyleProp<ViewStyle>;
    container1?: StyleProp<ViewStyle>;
    textColor1: StyleProp<TextStyle>;
    textColor2: StyleProp<TextStyle>;
    textColor3: StyleProp<TextStyle>;
    textColor4: StyleProp<TextStyle>;
    seperator: StyleProp<ViewStyle>;
    iconColor1: any;
    iconColor2: any;
    border: StyleProp<ViewStyle>;
    borderTopBottom: StyleProp<ViewStyle>;
    contentText: StyleProp<TextStyle>;
}

export const themeStyleMapper: { [key in ThemeType]: IThemeStyle } = {
    [ThemeType.light]: {
        container: {
            backgroundColor: Theme.WHITE,
        },
        textColor1: {
            color: Theme.BLACK,
        },
        textColor2: {
            color: Theme.GREY_DARK_MEDIUM,
        },
        textColor3: {
            color: Theme.GREY_DARK_MEDIUM,
        },
        textColor4: {
            color: Theme.CHARCOAL,
        },
        seperator: {
            backgroundColor: Theme.GREY_LIGHT,
        },
        iconColor1: {
            color: Theme.PRIMARY,
        },
        iconColor2: {
            color: Theme.BLACK,
        },
        border: {
            borderTopColor: Theme.GREY_LIGHT,
        },
        borderTopBottom: {
            borderTopColor: Theme.GREY_LIGHT,
            borderBottomColor: Theme.GREY_LIGHT,
        },
        contentText: {
            color: Theme.GREY_DARK_MEDIUM,
        },
    },
    [ThemeType.dark]: {
        container: {
            backgroundColor: Theme.BLACK,
        },
        container1: {
         backgroundColor: Theme.GREY_MEDIUM,
        },
        textColor1: {
            color: Theme.WHITE,
        },
        textColor2: {
            color: Theme.GREY_MEDIUM_LIGHT,
        },
        textColor3: {
            color: Theme.GREY_MEDIUM_LIGHT,
        },
        textColor4: {
            color: Theme.WHITE,
        },
        seperator: {
            backgroundColor: Theme.GREY_DARK,
        },
        iconColor1: {
            color: Theme.WHITE,
        },
        iconColor2: {
            color: Theme.WHITE,
        },
        border: {
            borderTopColor: Theme.GREY_DARK,
        },
        borderTopBottom: {
            borderTopColor: Theme.GREY_DARK,
            borderBottomColor: Theme.GREY_DARK,
        },
        contentText: {
            color: Theme.GREY_MEDIUM_LIGHT,
        },
    },
};

const mapStateToProps = (state: any) => {
    const { heartRateMonitorModalVisible } = state.ui;
    return {
        heartRateMonitorModalVisible,
        heartRateSetupStep: heartRateSetupStepSelector(state),
        changedTheme: themeTypeSelector(state),
        faqState: faqStateSelector(state),
    };
};

const mapDispatchToProps = (dispatch: any, ownProps: IProps) => {
    return {
        toggleModal: (visible: boolean) => dispatch(toggleHeartRateMonitorModalVisible(visible)),
        closeScreen: () => dispatch(closeSetupScreen()),
        disconnectHrMonitor: () => dispatch(disconnectHeartRateMonitor()),
        removeHrDeviceForUser: () => dispatch(removeHomeHeartRateMonitor(ownProps.updateUserHeartRateMonitors)),
        getCorrectInitialStep: () =>
            dispatch(getCorrectInitialSetupStep(ownProps.userProfile, ownProps.initialStepForUsersWithBelts)),
        goToStep: (step: HeartRateStep) => dispatch(goToSetupStep(step)),
        clearDeviceList: () => dispatch(clearHeartRateDeviceList()),
        setFaqActiveState: (faqState) => dispatch(setFaqState(faqState)),
        setTheme: (theme) => dispatch(setThemeType(theme)),
    };
};

export const HeartRateMonitorSetup = translate(['homeWorkouts'])(
  connect(mapStateToProps, mapDispatchToProps)(HeartRateMonitorSetupComponent));

export const ProfileHeartRateSetup = (props) => {
    return <HeartRateMonitorSetup
        consumer={ConsumerType.modal}
        themeType={ThemeType.light}
        {...props}>
    </HeartRateMonitorSetup>;
};
